import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import axios from '../../../../config/axios'
import StockContext from '../../../../contexts/StockContext'
import TableFiltersContext from '../../../../contexts/TableFiltersContext'
import ModalContext from '../../../../contexts/ModalContext'
import UserContext from '../../../../contexts/UserContext'
import editPen from '../../../../assets/edit-pen.svg'
import lookup from '../../../../assets/certificates/Tables/lookup.svg'
import CertificatesContext from '../../../../contexts/CertificatesContext'
import SectionContext from '../../../../contexts/SectionContext'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import ApiService from '../../../../services/ApiService'
import { useTenant } from '../../../../contexts/TenantContext'
import {
  DivInTd,
  GlobalTable,
  HeaderLine,
  Td,
  TdButtons,
  Th,
  Tr,
  FooterTr,
} from '../../../../components/styles/table-style'
import { ConfigMenuButton, ConfigMenuWrapper } from '../../SteelOrders/SteelOrdersTable/style'
import { TableContainer } from '../style'
import { useOutletContext } from 'react-router-dom'
import { StockOutletType } from '..'
import { TypeMainOutlet } from '../..'
import { Settings } from 'lucide-react'
import FieldsManager from '../../../../components/FieldManager'
import { formatReceivedDate } from './utils'

const ColorSwatchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const ColorSwatchCircle = styled.span<{ $hex?: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => {
    const hex = props.$hex;
    if (!hex) return '#bdbdbd';
    return hex.startsWith('#') ? hex : `#${hex}`;
  }};
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: inline-block;
  flex-shrink: 0;
`
interface Stock {
  id: number
  codeId: number
  material: string
  certificateId: number
  format: string
  finishing: string
  fabrication: string
  heatTreatment: string
  ncm: string
  originCountry: string
  supplier: string
  stockStreet: string
  shelf: string
  stockLocation: string
  quantity: string
  totalWeight: string
  dimension1: string
  dimension2: string
  lengthInMm: string
  costPerKg: string
  costPerMm: string
  code: string
  invoice: any[]
  totalCost: string
  density: string
  certificates: any[]
  availableBalance: string
  measurementUnit: string
  totalCmc: string
  unitCmc: string
  trackingNumber: string
  family: string
  description: string
  stockStatus: string
  characteristic: string
  itemColorId: number | null
  color: string
  pantoneCode: string
  pantoneHex: string
  receivedDate: string
  suppliers?: any[]
  purchaseOrder: string
  nf: string
  certificateNumber: string
  heatNumber: any
  createdAt: string
  updatedAt: string
  movimentType: string
  observation: string
  unitOfMeasurement: string
  isActivated: boolean
  certificateUrl: string | undefined
  prices?: any[]
  quantities?: any[]
}

const StockTable: React.FC = () => {
  const [permissions, setPermissions] = useState<string[]>([])
  const {
    stockQuantity,
    details,
    setStock,
    stockList,
    updateStockList,
    refresh,
    hasMoreStock,
    isLoading,
  } = useContext(StockContext)
  const { stockFilters, setStockFilters } = useContext(TableFiltersContext)
  const { setCertificate } = useContext(CertificatesContext)
  const { search } = useOutletContext<TypeMainOutlet>()
  const { setModalActive, setModalSection, setItIsUpdate, stockMode, setStockMode } =
    useContext(ModalContext)
  const { isFashionTenant } = useTenant()
  const isFashion = isFashionTenant || stockMode === 'fashion'
  const fashionHiddenFields = ['heatTreatment', 'density']
  const { user } = useContext(UserContext)
  const { setTabIndex, fields, setFields, isdropdownOpen, setIsDropdownOpen, setQuantity, quantity } =
    useOutletContext<StockOutletType>()
  const { setStockSection } = useContext(SectionContext)

  const columnMenuRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [setIsDropdownOpen])

  useEffect(() => {
    setStockSection('stock')
  }, [setStockSection])

  const [totalStockCount, setTotalStockCount] = useState<number | undefined>(
    undefined,
  )

  useEffect(() => {
    let mounted = true
    const fetchTotal = async () => {
      try {
        const resp = await ApiService.stock.getTotalStock()
        if (mounted && resp && resp.data != null) {
          const value =
            typeof resp.data === 'number'
              ? resp.data
              : resp.data.totalItems ?? resp.data.total ?? undefined
          setTotalStockCount(value)
        }
      } catch (e) {
        console.error(e)
      }
    }

    fetchTotal()

    return () => {
      mounted = false
    }
  }, [stockFilters, search, refresh])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateStockList(quantity, stockFilters, search)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [quantity, stockFilters, search, refresh])

  useEffect(() => {
    const fetchPermissions = async () => {
      const perms = await getPermissions()
      setPermissions(perms)
    }

    if (user.profileId) {
      fetchPermissions()
    }
  }, [user.profileId])

  const formatNumber = (num: number) => {
    return num.toFixed(2)
  }

  const handleCheckCertificate = async (certificateId: number) => {
    const certificateUrl = `${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates`
    axios
      .get(`${certificateUrl}/certificate/${certificateId}`)
      .then((response) => {
        setModalActive(true)
        setModalSection('viewCertificate')
        setCertificate(response.data)
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const filterStockData = (stock: Stock): Partial<Stock> => {
    const allowedFields = [
      'id',
      'codeId',
      'codeId',
      'code',
      'material',
      'format',
      'finishing',
      'fabrication',
      'heatTreatment',
      'originCountry',
      'supplier',
      'suppliers',
      'stockStreet',
      'shelf',
      'stockLocation',
      'ncm',
      'quantity',
      'totalWeight',
      'dimension1',
      'dimension2',
      'lengthInMm',
      'costPerKg',
      'costPerMm',
      'totalCost',
      'invoice',
      'density',
      'certificates',
      'availableBalance',
      'totalCmc',
      'trackingNumber',
      'receivedDate',
      'purchaseOrder',
      'nf',
      'certificateNumber',
      'heatNumber',
      'heatNumber',
      'family',
      'description',
      'stockStatus',
      'characteristic',
      'itemColorId',
      'color',
      'pantoneCode',
      'pantoneHex',
      'measurementUnit',
      'certificateUrl',
      'isActivated',
      'prices',
      'quantities',
    ] as const

    const filtered: any = {}
    allowedFields.forEach((key) => {
      if (key in stock) {
        filtered[key] = stock[key] ?? ''
      }
    })
    return filtered
  }

  const convertSupplierToString = (item: any) => {
    const newItem = { ...item }
    if (typeof item.supplier === 'object' && item.supplier !== null) {
      newItem.supplier = item.supplier?.name || ''
    }
    return newItem
  }

  const handleUpdateClick = (item: Stock) => {
    const filteredItem = filterStockData(item)
    if (filteredItem.receivedDate) {
      filteredItem.receivedDate = formatReceivedDate(filteredItem.receivedDate)
    }
    const convertedSupplier = convertSupplierToString(filteredItem)
    setStock(convertedSupplier)
    setItIsUpdate(true)
    if (isFashionTenant) setStockMode('fashion')
    setModalActive(true)
    setModalSection('firstCustomAdd')
  }

  const handleEntryOrExit = (item: Stock) => {
    const newItem = { ...item }
    if (newItem.receivedDate) {
      newItem.receivedDate = formatReceivedDate(newItem.receivedDate)
    }
    setStock({
      ...newItem,
      itemColorId: newItem.itemColorId ?? null,
      color: newItem.color ?? '',
      pantoneCode: newItem.pantoneCode ?? '',
      pantoneHex: newItem.pantoneHex ?? '',
    })
    setModalActive(true)
    setModalSection('modalEntryOrExit')
  }

  const handleNewModal = (item: any) => {
    const newItem = { ...item }
    if (newItem.receivedDate) {
      newItem.receivedDate = formatReceivedDate(newItem.receivedDate)
    }
    setStock({
      ...newItem,
      itemColorId: newItem.itemColorId ?? null,
      color: newItem.color ?? '',
      pantoneCode: newItem.pantoneCode ?? '',
      pantoneHex: newItem.pantoneHex ?? '',
    })
    if (isFashionTenant) setStockMode('fashion')
    setModalActive(true)
    setModalSection('newModal')
  }

  const getPermissions = async () => {
    try {
      const response = await axios(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/profile/find/${user.profileId}`,
      )

      if (response.data) {
        return response.data.permissions
      }
    } catch (error: any) {
      console.log('Usuário sem perfil')
    }
  }

  const TableComponent: React.FC = () => {
    return (
      <GlobalTable className='short'>
        <thead>
          <HeaderLine>
            {fields.whichFieldsToShow
              .filter((field) => field.checked)
              .filter((field) => !isFashion || !fashionHiddenFields.includes(field.field))
              .filter((field) => isFashion || field.field !== 'color')
              .map((field, index) => (
                <Th key={index}>{field.fieldName}</Th>
              ))}
            <Th>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'inline-block' }}>Ações</div>
                <ConfigMenuWrapper ref={columnMenuRef}>
                  <ConfigMenuButton onClick={() => setIsDropdownOpen((prev) => !prev)} title='Mostrar/ocultar colunas' style={{ color: '#bdbdbd' }}>
                    <Settings size={20} />
                  </ConfigMenuButton>
                  {isdropdownOpen && (
                    <FieldsManager
                      dropdownOpen={setIsDropdownOpen}
                      fields={{
                        ...fields,
                        whichFieldsToShow: fields.whichFieldsToShow
                          .filter(f => !isFashion ? f.field !== 'color' : true)
                          .filter(f => isFashion ? !fashionHiddenFields.includes(f.field) : true)
                      }}
                      setFields={setFields}
                    />
                  )}
                </ConfigMenuWrapper>
              </div>
            </Th>
          </HeaderLine>
        </thead>
        <tbody>
          {stockList.map((item, itemIndex) => {
            const rowClassName = [itemIndex === 0 ? 'first' : '', item.isActivated === false ? 'yellowLine' : '']
              .filter(Boolean)
              .join(' ')

            return (
              <Tr
                key={item.code}
                className={rowClassName}
                style={{ color: item.isActivated === false ? '#9d7d07' : '#000' }}
              >
                {fields.whichFieldsToShow
                  .filter((field) => field.checked)
                  .filter((field) => !isFashion || !fashionHiddenFields.includes(field.field))
                  .filter((field) => isFashion || field.field !== 'color')
                  .map((fieldObj, index) => {
                    const cellValue = item[fieldObj.field as keyof typeof item]

                    return (
                      <Td key={index}>
                        {fieldObj.field == 'certificateUrl' ||
                          fieldObj.field == 'certificates' ? (
                          (() => {
                            const certWithFile = item.HeatNumber?.Certificate?.find((certificate: any) => certificate.supplierFile != null && certificate.supplierFile !== '')
                            return certWithFile ? (
                              <a
                                href={certWithFile.supplierFile}
                                target='_blank'
                                rel='noopener noreferrer'
                                style={{ textDecoration: 'none', textAlign: 'center', color: '#0090d3', fontWeight: 'bold' }}
                              >
                                Visualizar
                              </a>
                            ) : null
                          })()
                        ) : fieldObj.field === 'description' ? (
                          <span
                            onClick={() =>
                              navigator.clipboard.writeText(cellValue as string)
                            }
                            style={{ cursor: 'pointer' }}
                            title='Clique para copiar'
                          >
                            {cellValue as string}
                          </span>
                        ) : fieldObj.field === 'lengthInMm' ||
                          fieldObj.field === 'totalWeight' ||
                          fieldObj.field === 'costPerKg' ||
                          fieldObj.field === 'costPerMm' ||
                          fieldObj.field === 'totalCost' ? (
                          cellValue !== null && cellValue !== undefined && cellValue !== ''
                            ? formatNumber(parseFloat(cellValue as string))
                            : ''
                        ) : fieldObj.field === 'receivedDate' ? (
                          formatReceivedDate(cellValue as string)
                        ) : fieldObj.field === 'color' ? (
                          <ColorSwatchContainer>
                            <ColorSwatchCircle $hex={item.pantoneHex} />
                            <span>
                              {cellValue as string}
                              {item.pantoneCode ? ` (${item.pantoneCode})` : ''}
                            </span>
                          </ColorSwatchContainer>
                        ) : (
                          (cellValue as any)?.name || cellValue
                        )}
                      </Td>
                    )
                  })}
                <TdButtons>
                  <img
                    src={lookup}
                    alt='look up icon'
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleNewModal(item)}
                  />
                </TdButtons>
              </Tr>
            )
          })}
        </tbody>
        <tfoot>
          <FooterTr>
            <Td
              colSpan={
                fields.whichFieldsToShow
                  .filter((f) => f.checked)
                  .filter((f) => !isFashion || !fashionHiddenFields.includes(f.field))
                  .length + 1
              }
              style={{
                textAlign: 'center',
                background: 'transparent',
                padding: '1rem 0',
              }}
            >
              <LoadMoreButton
                onLoadMore={() => setQuantity((prev) => prev + 50)}
                currentCount={stockList ? stockList.length : 0}
                totalCount={
                  typeof totalStockCount === 'number'
                    ? totalStockCount
                    : undefined
                }
                hasMore={hasMoreStock}
                isLoading={isLoading}
              />
            </Td>
          </FooterTr>
        </tfoot>
      </GlobalTable>
    )
  }

  let tableClass = ''
  if (stockFilters.length !== 0) {
    if (details) {
      tableClass = 'filteredTable'
    } else {
      tableClass = 'filteredTable shortTable'
    }
  } else {
    if (!details) {
      tableClass = 'shortTable'
    }
  }

  return (
    <TableContainer>
      <TableComponent />
    </TableContainer>
  )
}

export default StockTable
